import type {JourneyType} from '../chatbot/types';
import type {Router, RouteLocation} from 'src/types/router';
import type {State as ReduxState} from 'src/reducers';
import type {AtsAnalyticsField, EntityFilter} from 'src/types/ats-settings';
import type {UpdateOptions, AnalyticsQuery} from 'src/utils/analytics-query';
import type {ClientEventType} from 'src/types/events';
import type {AtsEntity} from 'src/types/ats-entities';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {connect, useDispatch} from 'react-redux';

import get from 'lodash/get';
import flow from 'lodash/flow';
import isEqual from 'lodash/isEqual';

import {formatPlural} from 'src/utils/intl';
import {queryIsEmpty} from 'src/utils/analytics-query';

import {getAtsFieldValues} from 'src/selectors/ats-settings';
import {
  selectEntityFilters,
  selectEntityFiltersForType,
  getCandidateFieldName,
  selectEntityMappingsByName,
} from 'src/selectors/ats-entities';
import {getCanEditSettings} from 'src/selectors/accounts';
import {getTrackingEvents} from 'src/selectors/report';
import {saveDefaultAnalyticsQuery} from 'src/action-creators/report';

import withAnalyticsQuery from 'src/components/analytics/with-query';
import {ClickAway} from '@spaced-out/ui-design-system/lib/utils/click-away';
import {FilterButton} from 'src/components/lib/analytics-filter-button';

import {
  MessageTypeCardV2,
  ResultCard,
  DeliveryStatusCard,
  RelatedToCard,
  RecipientCard,
  TrackingEventCard,
  SurveyAndChatbotCard,
  MoreFiltersCard,
  SentCard,
  DeliverySetToBouncedCard,
  SendWasSkippedCard,
  JourneyFilterCard,
  FilterCalendarStatusCard,
} from 'src/components/lib/analytics-filter-card';
import {SentReasonCard} from 'src/components/lib/analytics-filter-card/sent-card';
import TimeframeFilter from './timeframe-filter';

import css from './index.css';


type FilterType =
  | 'timeframe'
  | 'messagetype'
  | 'recipient'
  | 'relatedTo'
  | 'tracking_events'
  | 'more'
  | 'sentType'
  | 'result'
  | 'delivery_status'
  | 'event_type_filter'
  | 'journey'
  | 'filter_calendar_status';

export type FilterProps = {
  activeTabs: string[];
  query: Record<any, any>;
  onQueryChange: (update: Record<any, any>, options?: UpdateOptions) => void;
  router: Router;
  location: RouteLocation;
};

type OwnProps = {
  eventType?: ClientEventType;
  includeFilters?: FilterType[];
  extraFilters?: React.ComponentType<FilterProps>[];
  workflowEntityType?: string;
  entityType?: string;
  journeyList?: JourneyType[];
  minDate?: string;
  defaultTimeAgo?: string;
  showDefaultFilter?: boolean;
  className?: string;
  direction?: 'left' | 'right';
  showInternalUserFilter?: boolean;
};

type Props = OwnProps & {
  location: RouteLocation;
  router: Router;
  eventTypeCounts: {
    [key: string]: number;
  };
  query: Record<any, any>;
  onQueryChange: (update: Record<any, any>, options?: UpdateOptions) => void;
  entities: {
    [key: string]: AtsEntity;
  };
  atsFieldValues: {
    [id: string]: {
      values: AtsAnalyticsField[];
    };
  };
  entityFilters: Array<EntityFilter>;
  showSettingsLink: string | null | undefined;
  defaultQuery: AnalyticsQuery | null | undefined;
  agentDefaultFiltersEnabled: boolean;
  className?: string;
  journeyList?: JourneyType[];
  direction?: 'left' | 'right';
  showInternalUserFilter?: boolean;
};

// @ts-expect-error - TS7031 - Binding element 'entityType' implicitly has an 'any' type.
const mapStateToProps = (state: ReduxState, {entityType}): any => ({
  agentDefaultFiltersEnabled: state.agency.config.agentDefaultFiltersEnabled,
  showSettingsLink:
    (getCanEditSettings(state) && getCandidateFieldName(state)) || null,
  eventTypeCounts: state.eventTypeCounts.agency,
  entities: selectEntityMappingsByName(state),
  atsFieldValues: getAtsFieldValues(state),
  entityFilters: entityType
    ? selectEntityFiltersForType(state, entityType)
    : selectEntityFilters(state),
  defaultQuery: state.report.defaultQuery,
});

const AnalyticsFilters = ({
  className,
  location,
  query,
  router: history,
  eventType,
  eventTypeCounts,
  includeFilters = ['timeframe', 'recipient', 'more'],
  extraFilters = [],
  entities,
  atsFieldValues,
  entityFilters,
  onQueryChange,
  showSettingsLink,
  defaultQuery,
  agentDefaultFiltersEnabled,
  journeyList,
  minDate,
  defaultTimeAgo,
  showDefaultFilter = true,
  direction,
  showInternalUserFilter = false,
}: Props) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const defaultState: {
    activeTabs: string[];
    openFilter: string | null | undefined;
    lastFilterAction: 'none' | 'cleared' | 'restored' | 'saved';
  } = {
    activeTabs: [],
    openFilter: null,
    lastFilterAction: 'none',
  };
  const [state, setState] = React.useState(defaultState);
  const {activeTabs} = state;

  const dispatch = useDispatch();

  // @ts-expect-error - TS7019 - Rest parameter 'args' implicitly has an 'any[]' type.
  const localQueryChange = (...args) => {
    // @ts-expect-error - TS2556 - A spread argument must either have a tuple type or be passed to a rest parameter.
    onQueryChange(...args);
    setState({...state, lastFilterAction: 'none'});
  };

  const handleOpenFilter = (name: string) => () => {
    setState({...state, openFilter: name});
  };

  const queryAsArray = (
    key: string,
    options: UpdateOptions = {entityDataFilter: undefined},
  ) => {
    const getKey = options.entityDataFilter ? ['edf', key] : key;
    return get(query, getKey, []);
  };

  const queryAsValue = (
    key: string,
    options: UpdateOptions = {entityDataFilter: undefined},
  ) => {
    const getKey = options.entityDataFilter ? ['edf', key] : key;
    return get(query, getKey, null);
  };

  const _handleKeyedArrays = (
    key: string,
    evt: React.SyntheticEvent<HTMLInputElement>,
  ) => {
    const {name} = evt.currentTarget;
    const queryArray = queryAsArray(key);

    const queryArgs = queryArray.includes(name)
      ? // @ts-expect-error - TS7006 - Parameter 'type' implicitly has an 'any' type.
        queryArray.filter((type) => type !== name)
      : [...queryArray, name];

    localQueryChange({
      [key]: {$set: queryArgs},
    });
  };

  const handleMessageAndGoalTypeChange = _handleKeyedArrays.bind(
    this,
    'message_goal_type',
  );
  const handleResultTypeChange = _handleKeyedArrays.bind(this, 'result');
  const handleDeliveryStatusTypeChange = _handleKeyedArrays.bind(
    this,
    'delivery_status',
  );
  const handleRecipientTypeChange = _handleKeyedArrays.bind(
    this,
    'recipient_type',
  );

  const handleFilterCalendarStatusTypeChange = _handleKeyedArrays.bind(
    this,
    'filter_calendar_status',
  );

  const handleJourneyTypeChange = _handleKeyedArrays.bind(this, 'journey_type');

  const handleTrackingEventTypeChange = _handleKeyedArrays.bind(
    this,
    'tracking_events',
  );

  const handleRangeChange =
    (key: string) => (range: {start: number; end: number}) => {
      localQueryChange(
        {
          [key]: {$set: range},
        },
        {
          entityDataFilter: true,
        },
      );
    };

  const handleStringListChange = (key: string) => (values: string[]) => {
    localQueryChange(
      {
        [key]: {$set: values},
      },
      {entityDataFilter: true},
    );
  };

  const handleSurveyChatbotChange = (filter: React.SyntheticEvent) => {
    localQueryChange({
      event_type_filter: {$set: filter},
    });
  };

  const handleFromAddressChange = (vals: string[]) => {
    localQueryChange({
      ['from_address']: {$set: vals},
    });
  };

  const selectedMessageAndGoalTypes = queryAsArray('message_goal_type');
  const selectedResultTypes = queryAsArray('result');
  const selectedDeliveryStatusTypes = queryAsArray('delivery_status');
  const selectedRecipientTypes = queryAsArray('recipient_type');
  const selectedFilterCalendarStatusTypes = queryAsArray(
    'filter_calendar_status',
  );
  const selectedJourneyTypes = queryAsArray('journey_type');
  const selectedTrackingEventTypes = queryAsArray('tracking_events');
  const selectedSummaryFilterType = queryAsValue('event_type_filter') || null;
  const selectedFromAddresses = queryAsArray('from_address');

  const {
    client,
    recruiter,
    office,
    account_manager: accountManager,
    brand,
  } = atsFieldValues;

  const edfOption = {entityDataFilter: true} as const;

  const selectedClients = client
    ? // @ts-expect-error - TS2339 - Property 'senseAttribute' does not exist on type '{ values: AtsAnalyticsField[]; }'.
      queryAsArray(client.senseAttribute, edfOption)
    : [];
  const selectedOffices = office
    ? // @ts-expect-error - TS2339 - Property 'senseAttribute' does not exist on type '{ values: AtsAnalyticsField[]; }'.
      queryAsArray(office.senseAttribute, edfOption)
    : [];
  const selectedRecruiters = recruiter
    ? // @ts-expect-error - TS2339 - Property 'senseAttribute' does not exist on type '{ values: AtsAnalyticsField[]; }'.
      queryAsArray(recruiter.senseAttribute, edfOption)
    : [];
  const selectedAccountManagers = accountManager
    ? // @ts-expect-error - TS2339 - Property 'senseAttribute' does not exist on type '{ values: AtsAnalyticsField[]; }'.
      queryAsArray(accountManager.senseAttribute, edfOption)
    : [];
  const selectedBrands = brand
    ? // @ts-expect-error - TS2339 - Property 'senseAttribute' does not exist on type '{ values: AtsAnalyticsField[]; }'.
      queryAsArray(brand.senseAttribute, edfOption)
    : [];

  const showRelatedToFilter = shouldShowFilter(
    // @ts-expect-error - TS2345 - Argument of type '{ values: AtsAnalyticsField[]; }' is not assignable to parameter of type 'AtsAnalyticsField'.
    client,
    recruiter,
    office,
    accountManager,
    brand,
  );

  let entityFiltersTransformed = Object.keys(atsFieldValues).reduce<Array<any>>(
    (transformedFilters, fieldValueId) => {
      const fieldValue = atsFieldValues[fieldValueId];
      let entityFilter = entityFilters.find(
        ({id: entityFilterId}) => fieldValueId === entityFilterId,
      );
      if (entityFilter && fieldValue) {
        entityFilter = {
          ...entityFilter,
          // @ts-expect-error - TS2322 - Type '{ entity_type_display_name: string; sense_name: EntitySenseName; add_to_filters: boolean; attribute_name: string; category: string | null | undefined; data_type: "string" | ... 4 more ... | "phone"; ... 10 more ...; selected: string[] | ... 1 more ... | undefined; } | { ...; } | { ...; } | { ...; } | { ...; }' is not assignable to type 'EntityFilter | undefined'.
          entity_type_display_name:
            entities[entityFilter.entity_type]?.display_name,
          sense_name: entities[entityFilter.entity_type]?.sense_name,
        };
        const selected = queryAsArray(
          // @ts-expect-error - TS18048 - 'entityFilter' is possibly 'undefined'. | TS18048 - 'entityFilter' is possibly 'undefined'.
          `${entityFilter.entity_type}/${entityFilter.attribute_name}`,
          edfOption,
        );
        transformedFilters.push({
          ...entityFilter,
          ...fieldValue,
          selected,
        });
      }
      return transformedFilters;
    },
    [],
  );

  if (showInternalUserFilter) {
    entityFiltersTransformed = entityFiltersTransformed.filter(
      (item: any) => item?.sense_name === 'internal_employee',
    );
  }

  const entityFiltersActiveCount = entityFiltersTransformed.reduce(
    (prev, {selected}) => {
      if (selected) {
        if (Array.isArray(selected)) {
          return prev + selected.length;
        }
        return prev + 1;
      }
      return prev;
    },
    0,
  );

  const moreFiltersCount =
    entityFiltersActiveCount + selectedFromAddresses.length;
  const sentState = queryAsValue('show_filter') || 'sent_only';
  const deliveryStatusState = queryAsValue('delivery_status') || [];

  return (
    <div className={className}>
      {/* timeframe is always active until date-less queries happen? */}
      {includeFilters.includes('timeframe') && (
        <TimeframeFilter
          query={query}
          activeTabs={activeTabs}
          onQueryChange={localQueryChange}
          router={history}
          location={location}
          minDate={minDate}
          defaultTimeAgo={defaultTimeAgo}
          direction={direction}
        />
      )}
      {showRelatedToFilter && includeFilters.includes('relatedTo') && (
        <ClickAway>
          {({isOpen, onOpen, triggerRef, boundaryRef}) => (
            // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            <div className={css.filter} ref={triggerRef}>
              <FilterButton
                className={css.button}
                active={
                  activeTabs.includes('relatedTo') ||
                  selectedClients.length +
                    selectedRecruiters.length +
                    selectedBrands.length +
                    selectedAccountManagers.length +
                    selectedOffices.length >
                    0
                }
                activeFilters={
                  selectedClients.length +
                  selectedRecruiters.length +
                  selectedBrands.length +
                  selectedAccountManagers.length +
                  selectedOffices.length
                }
                onClick={() => {
                  handleOpenFilter('relatedTo');
                  onOpen();
                }}
              >
                {t('Related_To', 'Related To')}
              </FilterButton>
              {isOpen && (
                <div className={css.filterCard} ref={boundaryRef}>
                  {/* @ts-expect-error - TS2769 - No overload matches this call. */}
                  <RelatedToCard
                    history={history}
                    location={location}
                    client={client}
                    office={office}
                    recruiter={recruiter}
                    accountManager={accountManager}
                    brand={brand}
                    selectedClients={selectedClients}
                    selectedOffices={selectedOffices}
                    selectedRecruiters={selectedRecruiters}
                    selectedAccountManagers={selectedAccountManagers}
                    selectedBrands={selectedBrands}
                    onStringListChange={handleStringListChange}
                  />
                </div>
              )}
            </div>
          )}
        </ClickAway>
      )}
      {includeFilters.includes('recipient') && (
        <ClickAway>
          {({isOpen, onOpen, triggerRef, boundaryRef}) => (
            // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            <div className={css.filter} ref={triggerRef}>
              <FilterButton
                className={css.button}
                active={
                  activeTabs.includes('recipient') ||
                  selectedRecipientTypes.length > 0
                }
                activeFilters={selectedRecipientTypes.length}
                onClick={() => {
                  handleOpenFilter('recipient');
                  onOpen();
                }}
              >
                {t('Recipient_Type', 'Recipient Type')}
              </FilterButton>
              {isOpen && (
                <div className={css.filterCard} ref={boundaryRef}>
                  <RecipientCard
                    onRecipientTypeChange={handleRecipientTypeChange}
                    selectedRecipientTypes={selectedRecipientTypes}
                  />
                </div>
              )}
            </div>
          )}
        </ClickAway>
      )}
      {includeFilters.includes('filter_calendar_status') && (
        <ClickAway>
          {({isOpen, onOpen, triggerRef, boundaryRef}) => (
            // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            <div className={css.filter} ref={triggerRef}>
              <FilterButton
                className={css.button}
                active={
                  activeTabs.includes('filter_calendar_status') ||
                  selectedFilterCalendarStatusTypes.length > 0
                }
                activeFilters={selectedFilterCalendarStatusTypes.length}
                onClick={() => {
                  handleOpenFilter('filter_calendar_status');
                  onOpen();
                }}
              >
                {t('Filter_Calendar_Status', 'Filter Calendar Status')}
              </FilterButton>
              {isOpen && (
                <div className={css.filterCard} ref={boundaryRef}>
                  <FilterCalendarStatusCard
                    onFilterCalendarStatusTypeChange={
                      handleFilterCalendarStatusTypeChange
                    }
                    selectedFilterCalendarStatusTypes={
                      selectedFilterCalendarStatusTypes
                    }
                  />
                </div>
              )}
            </div>
          )}
        </ClickAway>
      )}
      {includeFilters.includes('journey') && (
        <ClickAway>
          {({isOpen, onOpen, triggerRef, boundaryRef}) => (
            // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            <div className={css.filter} ref={triggerRef}>
              <FilterButton
                className={css.button}
                active={
                  activeTabs.includes('journey') ||
                  selectedJourneyTypes.length > 0
                }
                activeFilters={selectedJourneyTypes.length}
                onClick={() => {
                  handleOpenFilter('journey');
                  onOpen();
                }}
              >
                {t('Journey_Type', 'Journey Type')}
              </FilterButton>
              {isOpen && (
                <div className={css.filterCard} ref={boundaryRef}>
                  <JourneyFilterCard
                    onJourneyTypeChange={handleJourneyTypeChange}
                    selectedJourneyTypes={selectedJourneyTypes}
                    journeyTypes={journeyList}
                  />
                </div>
              )}
            </div>
          )}
        </ClickAway>
      )}
      {includeFilters.includes('sentType') && (
        <ClickAway>
          {({isOpen, onOpen, triggerRef, boundaryRef}) => (
            // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            <div className={css.filter} ref={triggerRef}>
              <FilterButton
                className={css.button}
                active={activeTabs.includes('sentType')}
                // activeFilters={sentState && sentState !== 'both'}
                onClick={() => {
                  handleOpenFilter('sentType');
                  onOpen();
                }}
              >
                {t('Sent_or_Skipped', 'Sent or Skipped')}
              </FilterButton>
              {isOpen && (
                <div className={css.filterCard} ref={boundaryRef}>
                  <SentCard
                    value={sentState}
                    onChange={(v) => {
                      localQueryChange({
                        show_filter: {$set: v},
                        delivery_status: {$set: undefined},
                        result: {$set: undefined},
                        message_goal_type: {$set: undefined},
                      });
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </ClickAway>
      )}
      {includeFilters.includes('delivery_status') && (
        <ClickAway>
          {({isOpen, onOpen, triggerRef, boundaryRef}) => (
            // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            <div className={css.filter} ref={triggerRef}>
              <FilterButton
                className={css.button}
                active={
                  activeTabs.includes('delivery_status') ||
                  selectedDeliveryStatusTypes.length > 0
                }
                activeFilters={
                  selectedDeliveryStatusTypes.length > 1
                    ? selectedDeliveryStatusTypes.length
                    : 0
                }
                onClick={() => {
                  handleOpenFilter('delivery_status');
                  onOpen();
                }}
              >
                {t('Delivered', 'Delivered')}?
              </FilterButton>
              {isOpen && (
                <div className={css.filterCard} ref={boundaryRef}>
                  {sentState !== 'sent_only' ? (
                    <SentReasonCard
                      body={t(
                        'Set_Sent_or_Skipped_to_Sent',
                        'Set "Sent or Skipped?" to Sent',
                      )}
                    />
                  ) : (
                    <DeliveryStatusCard
                      onChange={handleDeliveryStatusTypeChange}
                      selectedTypes={selectedDeliveryStatusTypes}
                      sent={sentState}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </ClickAway>
      )}
      {includeFilters.includes('messagetype') && (
        <ClickAway>
          {({isOpen, onOpen, triggerRef, boundaryRef}) => (
            // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            <div className={css.filter} ref={triggerRef}>
              <FilterButton
                className={css.button}
                active={
                  activeTabs.includes('messagetype') ||
                  selectedMessageAndGoalTypes.length > 0
                }
                activeFilters={
                  selectedMessageAndGoalTypes.length > 1
                    ? selectedMessageAndGoalTypes.length
                    : 0
                }
                onClick={() => {
                  handleOpenFilter('messagetype');
                  onOpen();
                }}
              >
                {t('Message_Type_goal', 'Message Type & goal')}
              </FilterButton>
              {isOpen && (
                <div className={css.filterCard} ref={boundaryRef}>
                  <MessageTypeCardV2
                    onChange={handleMessageAndGoalTypeChange}
                    selectedTypes={selectedMessageAndGoalTypes}
                    showSkipped={sentState !== 'sent_only'}
                  />
                </div>
              )}
            </div>
          )}
        </ClickAway>
      )}
      {includeFilters.includes('result') && (
        <ClickAway>
          {({isOpen, onOpen, triggerRef, boundaryRef}) => (
            // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            <div className={css.filter} ref={triggerRef}>
              <FilterButton
                className={css.button}
                active={
                  activeTabs.includes('result') ||
                  selectedResultTypes.length > 0
                }
                activeFilters={
                  selectedResultTypes.length > 1
                    ? selectedResultTypes.length
                    : 0
                }
                onClick={() => {
                  handleOpenFilter('result');
                  onOpen();
                }}
              >
                {t('Converted', 'Converted')}?
              </FilterButton>
              {isOpen && (
                <div className={css.filterCard} ref={boundaryRef}>
                  {sentState === 'skipped_only' ? (
                    <SendWasSkippedCard />
                  ) : deliveryStatusState.length === 1 &&
                    deliveryStatusState[0] === 'bounced' ? (
                    <DeliverySetToBouncedCard />
                  ) : (
                    <ResultCard
                      onChange={handleResultTypeChange}
                      selectedTypes={selectedResultTypes}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </ClickAway>
      )}
      {includeFilters.includes('event_type_filter') && (
        <ClickAway>
          {({isOpen, onOpen, triggerRef, boundaryRef}) => (
            // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            <div className={css.filter} ref={triggerRef}>
              <FilterButton
                className={css.button}
                active={
                  activeTabs.includes('event_type_filter') ||
                  selectedSummaryFilterType != null
                }
                onClick={() => {
                  handleOpenFilter('event_type_filter');
                  onOpen();
                }}
              >
                {t('Surveys_chatbots', 'Surveys & chatbots')}
              </FilterButton>
              {isOpen && (
                <div className={css.filterCard} ref={boundaryRef}>
                  <SurveyAndChatbotCard
                    onTypeChange={handleSurveyChatbotChange}
                    selectedType={selectedSummaryFilterType}
                  />
                </div>
              )}
            </div>
          )}
        </ClickAway>
      )}
      {includeFilters.includes('tracking_events') && (
        <ClickAway>
          {({isOpen, onOpen, triggerRef, boundaryRef}) => (
            // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            <div className={css.filter} ref={triggerRef}>
              <FilterButton
                className={css.button}
                active={
                  activeTabs.includes('tracking_events') ||
                  selectedTrackingEventTypes.length > 0
                }
                activeFilters={selectedTrackingEventTypes.length}
                onClick={() => {
                  handleOpenFilter('tracking_events');
                  onOpen();
                }}
              >
                {t('Marketing', 'Marketing')}
              </FilterButton>
              {isOpen && (
                <div className={css.filterCard} ref={boundaryRef}>
                  <TrackingEventCard
                    onTrackingEventTypeChange={handleTrackingEventTypeChange}
                    trackingEventTypes={getTrackingEvents(
                      eventType || (query.event_types && query.event_types[0]),
                    )}
                    selectedTrackingEventTypes={selectedTrackingEventTypes}
                  />
                </div>
              )}
            </div>
          )}
        </ClickAway>
      )}
      {includeFilters.includes('more') && (
        <ClickAway>
          {({isOpen, onOpen, triggerRef, boundaryRef}) => (
            // @ts-expect-error - TS2322 - Type 'TriggerRefType<HTMLElement | null | undefined>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            <div className={css.filter} ref={triggerRef}>
              <FilterButton
                className={css.button}
                active={activeTabs.includes('more') || moreFiltersCount > 0}
                activeFilters={moreFiltersCount}
                onClick={() => {
                  handleOpenFilter('more');
                  onOpen();
                }}
              >
                {t('More_Filters', 'More Filters')}
              </FilterButton>
              {isOpen && (
                <div className={css.filterCard} ref={boundaryRef}>
                  {}
                  <MoreFiltersCard
                    filters={entityFiltersTransformed}
                    // @ts-expect-error - TS2322 - Type '(key: string) => (range: { start: number; end: number; }) => void' is not assignable to type '(key: string) => <T>(value: T) => unknown'.
                    onValueChange={handleRangeChange}
                    onStringListChange={handleStringListChange}
                    showSettingsLink={showSettingsLink}
                    onChangeFromAddress={handleFromAddressChange}
                    filteredFromAddresses={selectedFromAddresses}
                    direction={direction}
                    showInternalUserFilter={showInternalUserFilter}
                    // @ts-expect-error - TS17001 - JSX elements cannot have multiple attributes with the same name.
                    direction={direction}
                  />
                </div>
              )}
            </div>
          )}
        </ClickAway>
      )}
      {extraFilters.map((Filter) => (
        <Filter
          query={query}
          activeTabs={activeTabs}
          onQueryChange={localQueryChange}
          router={history}
          location={location}
        />
      ))}
      {state.lastFilterAction !== 'cleared' && !queryIsEmpty(query) && (
        <button
          className={css.textButton}
          onClick={() => {
            onQueryChange({$set: {}});
            setState({...state, lastFilterAction: 'cleared'});
          }}
        >
          {t('clear_filters', 'clear filters')}
        </button>
      )}
      {state.lastFilterAction !== 'none' && (
        <button className={css.textButton} disabled>
          {`${t('filters', 'filters')} `}
          {state.lastFilterAction}
        </button>
      )}
      {showDefaultFilter &&
        agentDefaultFiltersEnabled &&
        !isEqual(defaultQuery, query) &&
        state.lastFilterAction !== 'restored' && (
          <>
            <button
              className={css.textButton}
              onClick={() => {
                onQueryChange({
                  $merge: defaultQuery,
                });
                setState({...state, lastFilterAction: 'restored'});
              }}
            >
              {t('restore_saved_filters', 'restore saved filters')}
            </button>
            <button
              className={css.textButton}
              onClick={() => {
                dispatch(saveDefaultAnalyticsQuery(query));
                setState({...state, lastFilterAction: 'saved'});
              }}
            >
              {t('save_these_filters', 'save these filters')}
            </button>
          </>
        )}
    </div>
  );
};

export default flow(
  connect(mapStateToProps),
  withAnalyticsQuery,
)(AnalyticsFilters) as React.ComponentType<OwnProps>;

const humanTimeframeQuery = (query: any) => {
  if (query.time_ago !== undefined) {
    const timeAgo = JSON.parse(query.time_ago).days;
    if (timeAgo !== undefined) {
      return `Last ${timeAgo} ${formatPlural(timeAgo, 'day', 'days')}`;
    }
  }
  if (query.start_date && query.end_date) {
    return 'Custom Date Range';
  }
  if (!query.start_date && !query.end_date && !query.time_ago) {
    return 'Last 90 days';
  }
  return 'Timeframe';
};

const shouldShowFilter = (
  ...fieldValues: Array<AtsAnalyticsField | null | undefined>
): boolean =>
  fieldValues.reduce(
    (prev, curr) =>
      prev ||
      Boolean(curr && curr.values === null && curr.error && curr.message) ||
      Boolean(curr && Array.isArray(curr.values)) ||
      Boolean(curr && curr.min != null && curr.max != null),
    false,
  );
